/**
 * 周期规则对话框的**账户候选** —— 端侧纯函数（无渲染、无请求）
 *
 * ## 🔴 为什么要有这个文件
 *
 * 「某个 `kind` 能挂哪些账户」这件事，在批 `132` 之前是**页面里的一句内联 `filter`**
 * （`PlanPage` 传"内部挂账 + 贷款"给 `ScheduleDialog`）。加了 `template`（挂**任意实账户**，
 * 且**不判品类**，`DR-074` D3）之后，同一句 `filter` 就必须**按 kind 分派**——
 * 内联写法会变成"页面里一套、对话框里一套"的两份判据（铁律 24）。
 *
 * ⇒ 抽成纯函数：`kind → 候选账户`。测试直接跑函数（`renderToStaticMarkup` 拿不到
 * `Autocomplete` 的弹层，见技能 §4.6 的已知限制）。
 *
 * ## ⚠️ 它**不是**形态判定
 *
 * 真正的判定在 `@jiaying/shared` 的 `checkScheduleShape()`（服务端 `400` 的 `message` 与端侧
 * 转发的是**同一条文案**）。本文件只决定"下拉里给用户看什么"——
 * **给多了**：服务端会拒并说明原因；**给少了**：用户以为这件事做不了。
 * 所以这里的口径一律**从宽**，与服务端"该放的放"保持一致。
 */

import { AccountType, ContraKind, InternalCategory, ScheduleKind, type AccountView } from '@jiaying/shared';

/**
 * 某个 `kind` 允许绑定的账户候选（保序：调用方传入的顺序原样返回）。
 *
 * | `kind` | 候选 | 依据 |
 * |---|---|---|
 * | `recurring` | `internal / payable`（待缴） | `AC-F1.3` |
 * | `amortization` | `internal / amortize`（待摊） | `AC-F1.4` |
 * | `loan` | `type = loan`，或 `contra` 且 `contraKind = loan` | `DR-044` D7（两类贷款账户） |
 * | 🆕 `template` | **全部账户** | `DR-074` D3：不判品类 —— 储蓄卡 / 信用卡 / 电子钱包 / 社保公积金 / 投资账户都该能用 |
 * | `installment` | **空**（P1 才启用） | `AC-F1.1` |
 */
export function accountCandidatesFor(
  kind: ScheduleKind,
  accounts: readonly AccountView[],
): readonly AccountView[] {
  switch (kind) {
    case ScheduleKind.Recurring:
      return accounts.filter(
        (a) => a.type === AccountType.Internal && a.category === InternalCategory.Payable,
      );
    case ScheduleKind.Amortization:
      return accounts.filter(
        (a) => a.type === AccountType.Internal && a.category === InternalCategory.Amortize,
      );
    case ScheduleKind.Loan:
      return accounts.filter(
        (a) =>
          a.type === AccountType.Loan ||
          (a.type === AccountType.Contra && a.contraKind === ContraKind.Loan),
      );
    case ScheduleKind.Template:
      // 🔴 从宽：服务端不判品类（`DR-074` D3），端侧也不该先替它拒
      return accounts;
    default:
      // `installment`：P1 才启用；未知 kind 同样给空（宁可少给，也不要给出会被 400 的候选）
      return [];
  }
}

/**
 * 新建时按**账户品类**推断默认 `kind`（待摊 ⇒ 摊销；待缴 ⇒ 周期账；其余 ⇒ 周期记账模板）。
 *
 * ⚠️ 只是**默认值**，用户可改；最终能不能挂由服务端判。
 * 🔴 第三档刻意选 `template` 而不是 `recurring`：储蓄卡 / 信用卡这类实账户上唯一说得通的
 * 周期规则就是「到期直接记一笔」（`recurring` 会被内核以"只能绑定待缴账户"拒掉）。
 */
export function defaultKindFor(account: AccountView | undefined): ScheduleKind {
  if (account === undefined) return ScheduleKind.Recurring;
  if (account.type === AccountType.Internal && account.category === InternalCategory.Amortize) {
    return ScheduleKind.Amortization;
  }
  if (account.type === AccountType.Internal && account.category === InternalCategory.Payable) {
    return ScheduleKind.Recurring;
  }
  return ScheduleKind.Template;
}
